<template>
  <application-layout>
    <v-container>
      <h1>User Index</h1>
      <v-layout justify-space-between>
        <v-flex xs4>
          <application-modal v-model="addUserModal" @collapse="clearUser">
            <v-btn
              data-testid="add-new-user-btn"
              class="add-new-user-btn mb-2 ml-0 primary"
              slot="modal-trigger"
              @click.native="addUserModal = true"
            >
              <v-icon>add</v-icon>
              Add New User
            </v-btn>
            <div slot="modal-title">Add New User</div>
            <div slot="modal-body">
              <v-card-text>
                <v-form ref="form">
                  <app-input
                    data-testid="new-user-email-input"
                    class="new-user-email-input"
                    v-model.trim="newUser.email"
                    type="text"
                    label="Email Address"
                    :rules="{ required: true, email: true }"
                    filled
                  >
                    Email Address
                  </app-input>
                  <v-flex>
                    <v-select
                      data-testid="new-user-roles-menu"
                      class="new-user-roles-menu"
                      v-model="userRoles"
                      :items="availableRoles"
                      label="User Roles"
                      :rules="inputRules"
                      item-text="name"
                      item-value="id"
                      hint="Roles determine what a user is able to do in the call center platform"
                      persistent-hint
                      filled
                      required
                      chips
                      multiple
                    />
                  </v-flex>
                  <app-input
                    data-testid="new-user-email-input"
                    class="new-user-email-input"
                    v-model="newUser.ctm_id"
                    type="text"
                    label="CTM ID"
                    :rules="{ required: true }"
                    filled
                  >
                    CTM ID
                  </app-input>
                  <app-input
                    data-testid="new-user-avatar-input"
                    class="new-user-avatar-input"
                    v-model="newUser.avatar_url"
                    type="text"
                    label="Avatar URL"
                    :rules="{ required: true, max: 255 }"
                    filled
                  >
                    Avatar URL
                  </app-input>
                </v-form>
              </v-card-text>
            </div>
            <v-btn
              data-testid="new-user-add-btn"
              class="new-user-add-btn"
              color="primary"
              slot="create-button"
              :loading="loading"
              :disabled="!formValid"
              @click="submitUserForm"
            >
              Add User
            </v-btn>
          </application-modal>
        </v-flex>
        <v-flex xs6 />
        <v-flex xs2>
          <v-switch
            data-testid="toggle-users-switch"
            class="toggle-users-switch"
            v-model="toggleSwitch"
            :label="toggleSwitch ? 'Display Only Active Users' : 'Display Disabled Users'"
            @change="handleToggledUsers"
          />
        </v-flex>
      </v-layout>
      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        :no-data-text="loading ? 'Loading users...' : 'There are no users to display'"
      >
        <template slot="item" slot-scope="props">
          <tr>
          <td>{{ props.item.id }}</td>
          <td v-if="props.item.registered">{{ props.item.username }}</td>
          <td v-if="props.item.registered">{{ props.item.first_name }} {{ props.item.last_name }}</td>
          <td v-if="!props.item.registered" align="center" colspan="2">User has not finished registration</td>
          <td>{{ props.item.email }}</td>
          <td>
            <v-btn
              data-testid="edit-user-btn"
              class="edit-user-btn mr-2"
              :to="{ name: 'usersEdit', params: { userId: props.item.id } }"
              small
              color="primary"
            >
              Edit
            </v-btn>
            <v-btn
              data-testid="user-activate-disable-btn"
              class="user-activate-disable-btn mx-2"
              :class="props.item.enabled ? 'error' : 'success'"
              small
              @click="openStatusModal(props.item)"
            >
              <span v-if="props.item.enabled === 1">Disable</span>
              <span v-else>Activate</span>
            </v-btn>
          </td>
          </tr>
        </template>
      </v-data-table>
    </v-container>

    <application-modal v-model="toggleStatusModal" @collapse="clearUser">
      <div slot="modal-title">Toggle User Status</div>
      <div slot="modal-body">
        <v-layout column class="subheading">
          <v-flex>Are you sure you want to
            <span v-if="user.enabled === 1">disable</span>
            <span v-else>activate</span>
            <span v-if="user.first_name || user.last_name">&nbsp;{{ user.first_name }} {{ user.last_name }}?</span>
            <span v-else>&nbsp;this user?</span>
          </v-flex>
          <v-flex class="warning--text mt-3">This action can be undone.</v-flex>
        </v-layout>
      </div>
      <v-btn
        data-testid="toggle-user-status-btn"
        class="toggle-user-status-btn"
        color="primary"
        slot="create-button"
        :loading="loading"
        @click="toggleDisable(user)"
      >
        <span v-if="user.enabled === 1">Disable</span>
        <span v-else>Activate</span>
      </v-btn>
    </application-modal>
  </application-layout>
</template>

<script>
import { avatarRules, emailRules, inputRules } from "@/helpers/form-rules";
import ApplicationLayout from "@/layouts/ApplicationLayout";
import ApplicationModal from "@/components/ApplicationModal";
import { mapState } from "vuex";
import { userIndexHeaders } from "@/helpers/user/user-index-data-table-headers";

export default {
  data() {
    return {
      loading: false,
      addUserModal: false,
      toggleStatusModal: false,
      toggleSwitch: false,
      availableRoles: [],
      user: "",
      newUser: {
        email: "",
        assigned_roles: [],
        ctm_id: "",
        avatar_url: ""
      },

      avatarRules: avatarRules,
      emailRules: emailRules,
      inputRules: inputRules,

      headers: userIndexHeaders
    };
  },

  methods: {
    async getAllUsers() {
      this.loading = true;
      await this.$store.dispatch("getAllUsers", this.toggleSwitch);
      this.loading = false;
    },

    getCallCenterRoles() {
      this.$store.dispatch("getCallCenterRoles")
        .then(resp => {
          this.availableRoles = resp.data.roles;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    submitUserForm() {
      this.loading = true;
      this.$store.dispatch("createUser", this.newUser)
        .then(() => {
          this.getAllUsers();
          this.clearUser();
          this.loading = false;
          this.addUserModal = false;
          this.$refs.form.reset();
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    clearUser() {
      this.newUser.email = "";
      this.userRoles = [];
      this.user = "";
    },

    openStatusModal(user) {
      this.user = user;
      this.toggleStatusModal = true;
    },

    toggleDisable(user) {
      this.loading = true;
      this.$store.dispatch("toggleUserProfile", user)
        .then(() => {
          this.loading = false;
          this.$store.dispatch("getAllUsers", this.toggleSwitch);
          this.toggleStatusModal = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    async handleToggledUsers() {
      this.loading = true;
      await this.$store.dispatch("getAllUsers", this.toggleSwitch);
      this.loading = false;
    }
  },

  computed: {
    ...mapState({
      users: state => state.user.users
    }),
    formValid() {
      return !!(this.newUser.email &&
        this.newUser.ctm_id &&
        this.newUser.avatar_url &&
        this.newUser.assigned_roles.length);
    },
    userRoles: {
      get() {
        return this.newUser.assigned_roles;
      },
      set(newValue) {
        this.newUser.assigned_roles = newValue;
      }
    }
  },

  created() {
    this.getAllUsers();
    this.getCallCenterRoles();
  },

  components: {
    ApplicationLayout,
    ApplicationModal
  }
};
</script>

<style scoped>
.toggle-users-switch {
  margin-left: 12%;
}
</style>
